import { createContext, useContext } from "react";

/**
 * The app's one right panel is a stack of steps: a deal, a to-do. Each step is a drawer that renders
 * into the host's single frame (`panel-stack.tsx`) instead of opening a frame of its own, so a to-do
 * opened from a deal replaces the deal in the same panel, with a back link to it.
 *
 * Every step a person opens is a history entry. Its state names the step on top (`panelTop`), the step
 * it was opened from (`panelBack`, the back link's words) and how many panel entries sit on the stack
 * (`panelDepth`), so Back (the link, Esc, the browser) returns one step and Close pops them all.
 */
export type PanelKind = "deal" | "todo" | "contact" | "job";

/** The record kinds a row opens in the panel (`contexts/record-panel-context.tsx`). */
export type RecordKind = Extract<PanelKind, "deal" | "contact" | "job">;

/** Each step kind's URL param, so a reload or a shared link opens the step on top, and the record page
 *  path a plain link click opens in the panel instead (`useRecordLinkClick`). A job is `?job=<tenant>:<st_job_id>`
 *  (`JobPanel`) and has no page path. A contact is `?contact_tray=<contact id>` (`ContactPanel`): `contact_tray`,
 *  not `contact`, because the Contact Command Center's own list filter is `?contact=`, and Close clears every step
 *  param. */
export const PANEL_STEPS: Record<PanelKind, { param: string; path?: RegExp }> = {
  deal: { param: "deal", path: /^\/deals\/([0-9a-f-]{36})$/i },
  todo: { param: "todo" },
  contact: { param: "contact_tray" },
  job: { param: "job" },
};

/** The step kinds, in slot order. */
export const PANEL_KINDS = Object.keys(PANEL_STEPS) as PanelKind[];

/** One step on the open path: its kind, and its record when it has one (a create form has none). */
export interface PanelPathStep {
  kind: PanelKind;
  id?: string;
  /** Where Back went from this step, so a step brought back on top in place (a rebased entry) names it again. */
  back?: PanelBackTarget;
}

/** Where a step's Back goes: the step it was opened from, by kind, record, name and brand
 *  ("‹ PT Deal · Richard Staab - Air Source Heat Pump"). */
export interface PanelBackTarget {
  /** Unknown only on an entry written before back targets carried it. */
  kind?: PanelKind;
  id?: string;
  label: string;
  /** The brand family's code, when the step's header shows one. */
  brand?: string | null;
}

export interface PanelHistoryState {
  panelTop?: PanelKind;
  /** Where Back goes. A plain string on entries written before the back target carried its kind and record. */
  panelBack?: PanelBackTarget | string;
  panelDepth?: number;
  /** The entry is a step held outside the URL (a create form), pushed with the URL unchanged. */
  panelLocal?: boolean;
  /** Every panel entry from the first to this one, this entry's own step last (one per `panelDepth`), so opening a
   *  record already on the path goes back to it instead of stacking it again. It may lead with one more step: the
   *  record a shared link opened on the entry beneath the first push (`?deal=<id>` from Slack). */
  panelPath?: PanelPathStep[];
  /** A page rewrote its query (a board filter) while steps were open, and the panel re-stamped that entry with its
   *  stack: the steps behind it are not history entries with this filter, so Back moves in place (replace) and never
   *  undoes the filter. */
  panelRebased?: boolean;
  /** The depth of the last rebased entry; entries pushed after it may go back to it through history, not past it. */
  panelRebaseDepth?: number;
  /** A Back that moved in place (a rebased entry): the step it brings back keeps its scroll, as after a history Back. */
  panelReturn?: boolean;
}

/** The panel's own history fields, so closing the whole stack can drop them and keep the page's. */
export const PANEL_STATE_KEYS = [
  "panelTop",
  "panelBack",
  "panelDepth",
  "panelLocal",
  "panelPath",
  "panelRebased",
  "panelRebaseDepth",
  "panelReturn",
] as const satisfies readonly (keyof PanelHistoryState)[];

/** An entry's back target, whichever shape it was written in. */
export function panelBackTarget(state: PanelHistoryState | null | undefined): PanelBackTarget | null {
  const back = state?.panelBack;
  if (!back) return null;
  return typeof back === "string" ? { label: back } : back;
}

/** The history state for a step opened on top of the current entry; `back` names the step on screen, `step` the
 *  one being opened. */
export function panelPushState(
  current: unknown,
  kind: PanelKind,
  back?: PanelBackTarget | null,
  step?: PanelPathStep,
): PanelHistoryState {
  const prev = (current && typeof current === "object" ? current : {}) as PanelHistoryState;
  const depth = prev.panelDepth ?? 0;
  // A path that lost step with the depth (an entry written before paths) starts again from here. The first push over
  // a record a shared link opened (no panel entry of its own) leads with that record, so a drill-in back to it is Back.
  const path = validPath(prev)
    ? prev.panelPath!
    : depth === 0 && back?.kind && back.id
      ? [{ kind: back.kind, id: back.id }]
      : [];
  return {
    ...prev,
    panelTop: kind,
    panelBack: back ?? undefined,
    panelDepth: depth + 1,
    panelLocal: undefined,
    panelRebased: undefined,
    panelReturn: undefined,
    panelPath: [...path, { ...(step ?? { kind }), back: back ?? undefined }],
  };
}

/** A path in step with its entry's depth: one step per panel entry, plus at most the shared link's record ahead. */
export function validPath(state: PanelHistoryState): boolean {
  const depth = state.panelDepth ?? 0;
  const length = state.panelPath?.length;
  return length !== undefined && depth > 0 && (length === depth || length === depth + 1);
}

/** How many entries back the record `{kind, id}` sits on the open path (its nearest earlier entry), or null when it
 *  is not on it. Opening it from inside the panel goes back that many steps. */
export function stepsBackTo(state: PanelHistoryState | null | undefined, kind: PanelKind, id: string): number | null {
  if (!state || !validPath(state)) return null;
  const path = state.panelPath!;
  for (let i = path.length - 2; i >= 0; i--) {
    if (path[i].kind === kind && path[i].id === id) return path.length - 1 - i;
  }
  return null;
}

/** What a step's drawer asks of the frame while it is on top. */
export interface PanelFrame {
  fullHeight?: boolean;
  /** Opt in to the phone's large detent (`ResponsiveDrawer`'s `mobileSnapToTop`). Every record step gets it from the
   *  frame anyway; a non-record step may ask for it. */
  mobileSnapToTop?: boolean;
  /** The step's desktop width cap while it is on top (the deal panel widens while an email is being written). */
  desktopWidthClass?: string;
  expandable?: boolean;
  expandsByDefault?: boolean;
  expandResetKey?: string | boolean | null;
  nonModal?: boolean;
  koryTodoId?: string;
  desktopAutoFocus?: (e: Event) => void;
}

export interface PanelEntry {
  label: string;
  /** The record on screen and its brand code, so the next step's back link names it by kind and brand. */
  record?: { id: string; brand?: string | null };
  /** A record's current name by id (a rename since the back link was written), when this step can say. */
  labelOf?: (id: string) => string | null | undefined;
  /** Held outside the URL (a create form, a local row): the host pushes its history entry. */
  local?: boolean;
  frame: PanelFrame;
  /** The step's own close: one step back, through the drawer's opener. */
  close: () => void;
  /** Saves what the step holds back until it closes (a staged owner change), without closing it. */
  flush?: () => void;
}

export interface PanelStackHost {
  register: (kind: PanelKind, entry: PanelEntry | null) => void;
  /** The node a step portals its body into, once the frame has mounted it. */
  slots: Partial<Record<PanelKind, HTMLElement | null>>;
  /** A step is on screen. */
  isOpen: boolean;
  /** The step on top, and its name for the next step's back link. */
  top: PanelKind | null;
  topLabel: string | null;
  /** The step on top as the next step's back target. */
  topBack: PanelBackTarget | null;
  /** A drill-in to `{kind, id}` when it is already on the open path: goes back to it (history, or in place on a
   *  rebased entry) and returns true. False: it is not on the path, so the caller opens it as a new step. */
  goBackTo: (kind: PanelKind, id: string) => boolean;
  /** Closes the step on top of a rebased entry in place: back to the step beneath it, or every step cleared when it
   *  was the last, so the closed step leaves the path too. False off a rebased entry: the caller pops its own entry. */
  closeTopInPlace: () => boolean;
}

export const PanelStackContext = createContext<PanelStackHost | null>(null);

/** Null outside the app shell (embeds, harnesses, tests): drawers keep a frame of their own there. */
export function usePanelStack(): PanelStackHost | null {
  return useContext(PanelStackContext);
}
